import React, { useCallback, useEffect, useState } from 'react';
import {
  AdminReviewApiError,
  createMotionClaimPublicationCandidate,
  createMotionClaimReportInsertionCandidate,
  fetchMotionClaimProjectionDryRun,
  fetchMotionClaimPublicationCandidates,
  fetchMotionClaimReportInsertionCandidates,
  normalizeReviewerId,
  revokeMotionClaimPublicationCandidate,
  revokeMotionClaimReportInsertionCandidate,
  type MotionProjectionDryRunResponse,
  type MotionProjectionDryRunStatement,
  type MotionProjectionExcludedClaim,
  type MotionPublicationCandidate,
  type MotionReportInsertionCandidate,
} from '../../lib/adminReviewApi';

export interface MotionProjectionDryRunPanelViewProps {
  uploadId: string;
  payload: MotionProjectionDryRunResponse | null;
  loading: boolean;
  error: string | null;
  publisherId: string;
  onPublisherIdChange: (value: string) => void;
  publicationCandidatesByWindow: Record<string, MotionPublicationCandidate>;
  insertionCandidatesByWindow: Record<string, MotionReportInsertionCandidate>;
  actionLoadingWindowId?: string | null;
  cardSuccessMessages?: Record<string, string>;
  insertionCardSuccessMessages?: Record<string, string>;
  onCreateCandidate: (windowId: string) => void;
  onRevokeCandidate: (windowId: string) => void;
  onCreateInsertionCandidate: (windowId: string) => void;
  onRevokeInsertionCandidate: (windowId: string) => void;
}

const InternalOnlyBadge: React.FC = () => (
  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
    Internal only
  </span>
);

const NotPublishedBadge: React.FC = () => (
  <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-900 dark:bg-red-900/40 dark:text-red-200">
    Not published
  </span>
);

function formatExclusionReason(reason: string): string {
  return reason.replace(/_/g, ' ');
}

function publicationStatusLabel(status?: string): string {
  if (status === 'internal_publish_candidate') return 'Internal publish candidate';
  if (status === 'revoked') return 'Revoked';
  return 'No internal candidate';
}

export function insertionStatusLabel(status?: string): string {
  if (status === 'customer_report_insertion_candidate') return 'Customer report insertion candidate';
  if (status === 'customer_report_revoked') return 'Customer report insertion candidate revoked';
  return 'No report insertion candidate';
}

export const MotionProjectionDryRunPanelView: React.FC<MotionProjectionDryRunPanelViewProps> = ({
  uploadId,
  payload,
  loading,
  error,
  publisherId,
  onPublisherIdChange,
  publicationCandidatesByWindow,
  insertionCandidatesByWindow,
  actionLoadingWindowId,
  cardSuccessMessages = {},
  insertionCardSuccessMessages = {},
  onCreateCandidate,
  onRevokeCandidate,
  onCreateInsertionCandidate,
  onRevokeInsertionCandidate,
}) => {
  const publisherReady = normalizeReviewerId(publisherId).length > 0;

  const renderProjectedStatement = (statement: MotionProjectionDryRunStatement) => {
    const candidate = publicationCandidatesByWindow[statement.window_id];
    const insertionCandidate = insertionCandidatesByWindow[statement.window_id];
    const isActivePublishCandidate = candidate?.publication_status === 'internal_publish_candidate';
    const isActiveInsertionCandidate =
      insertionCandidate?.insertion_status === 'customer_report_insertion_candidate';
    const cardBusy = actionLoadingWindowId === statement.window_id;

    return (
      <article
        key={statement.window_id}
        className="rounded-lg border border-secondary-200 p-3 dark:border-secondary-700"
        data-testid={`dry-run-statement-${statement.window_id}`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs text-secondary-500 dark:text-secondary-400">
            {statement.window_id}
          </span>
          <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-900 dark:bg-violet-900/40 dark:text-violet-200">
            {statement.approval_status.replace(/_/g, ' ')}
          </span>
          <span
            className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-900 dark:bg-sky-900/40 dark:text-sky-200"
            data-testid={`publication-status-${statement.window_id}`}
          >
            {publicationStatusLabel(candidate?.publication_status)}
          </span>
          <span
            className="rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-900 dark:bg-teal-900/40 dark:text-teal-200"
            data-testid={`insertion-status-${statement.window_id}`}
          >
            {insertionStatusLabel(insertionCandidate?.insertion_status)}
          </span>
        </div>
        <p className="mt-2 text-sm text-secondary-900 dark:text-white">{statement.statement}</p>
        <dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-secondary-600 dark:text-secondary-300">
          <div>
            <dt className="font-medium text-secondary-500 dark:text-secondary-400">Concept</dt>
            <dd>{statement.concept_display}</dd>
          </div>
          <div>
            <dt className="font-medium text-secondary-500 dark:text-secondary-400">Attack area</dt>
            <dd>{statement.attack_area_display}</dd>
          </div>
          <div>
            <dt className="font-medium text-secondary-500 dark:text-secondary-400">Confidence</dt>
            <dd>{statement.confidence_label}</dd>
          </div>
          <div>
            <dt className="font-medium text-secondary-500 dark:text-secondary-400">Evidence</dt>
            <dd>{statement.evidence_label}</dd>
          </div>
          {statement.reviewer_id ? (
            <div className="col-span-2">
              <dt className="font-medium text-secondary-500 dark:text-secondary-400">Reviewer</dt>
              <dd className="font-mono">{statement.reviewer_id}</dd>
            </div>
          ) : null}
        </dl>

        {cardSuccessMessages[statement.window_id] ? (
          <p
            className="mt-3 rounded bg-emerald-50 p-2 text-xs text-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200"
            data-testid={`publication-success-${statement.window_id}`}
          >
            {cardSuccessMessages[statement.window_id]}
          </p>
        ) : null}

        {insertionCardSuccessMessages[statement.window_id] ? (
          <p
            className="mt-3 rounded bg-emerald-50 p-2 text-xs text-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200"
            data-testid={`insertion-success-${statement.window_id}`}
          >
            {insertionCardSuccessMessages[statement.window_id]}
          </p>
        ) : null}

        <div className="mt-4 space-y-2 border-t border-secondary-200 pt-3 dark:border-secondary-700">
          <p
            className="text-xs text-amber-900 dark:text-amber-200"
            data-testid={`publication-warning-${statement.window_id}`}
          >
            This creates an internal publish candidate only. It does not publish to customer
            reports, Evidence Reel, or delivery.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary text-xs disabled:opacity-50"
              disabled={!publisherReady || isActivePublishCandidate || cardBusy}
              data-testid={`create-candidate-${statement.window_id}`}
              onClick={() => onCreateCandidate(statement.window_id)}
            >
              Create internal publish candidate
            </button>
            <button
              type="button"
              className="btn-secondary text-xs disabled:opacity-50"
              disabled={!publisherReady || !isActivePublishCandidate || cardBusy}
              data-testid={`revoke-candidate-${statement.window_id}`}
              onClick={() => onRevokeCandidate(statement.window_id)}
            >
              Revoke internal publish candidate
            </button>
          </div>
        </div>

        <div className="mt-4 space-y-2 border-t border-secondary-200 pt-3 dark:border-secondary-700">
          <p
            className="text-xs text-amber-900 dark:text-amber-200"
            data-testid={`insertion-warning-${statement.window_id}`}
          >
            This creates an internal customer report insertion candidate only. It does not publish
            to the customer report, /full, Evidence Reel, delivery, or exports.
          </p>
          {!isActivePublishCandidate ? (
            <p
              className="text-xs text-secondary-600 dark:text-secondary-300"
              data-testid={`insertion-prerequisite-${statement.window_id}`}
            >
              Requires active internal publish candidate first.
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary text-xs disabled:opacity-50"
              disabled={
                !publisherReady || !isActivePublishCandidate || isActiveInsertionCandidate || cardBusy
              }
              data-testid={`create-insertion-candidate-${statement.window_id}`}
              onClick={() => onCreateInsertionCandidate(statement.window_id)}
            >
              Create insertion candidate
            </button>
            <button
              type="button"
              className="btn-secondary text-xs disabled:opacity-50"
              disabled={!publisherReady || !isActiveInsertionCandidate || cardBusy}
              data-testid={`revoke-insertion-candidate-${statement.window_id}`}
              onClick={() => onRevokeInsertionCandidate(statement.window_id)}
            >
              Revoke insertion candidate
            </button>
          </div>
        </div>
      </article>
    );
  };

  const renderExcludedClaim = (claim: MotionProjectionExcludedClaim) => (
    <div
      key={claim.window_id}
      className="rounded border border-secondary-200 p-2 text-xs dark:border-secondary-700"
      data-testid={`dry-run-excluded-${claim.window_id}`}
    >
      <div className="font-mono text-secondary-500 dark:text-secondary-400">{claim.window_id}</div>
      <div className="mt-1 text-secondary-700 dark:text-secondary-200">
        Reason: {formatExclusionReason(claim.reason)}
      </div>
      {claim.warnings.length > 0 ? (
        <div className="mt-1 text-secondary-600 dark:text-secondary-300">
          Warnings: {claim.warnings.join(', ')}
        </div>
      ) : null}
    </div>
  );

  if (loading) {
    return (
      <section className="rounded-lg border border-secondary-200 p-4 text-sm text-secondary-600 dark:border-secondary-700 dark:text-secondary-300">
        Loading customer projection dry run…
      </section>
    );
  }

  if (error) {
    return (
      <section
        className="rounded-lg border border-secondary-200 p-4 text-sm text-secondary-600 dark:border-secondary-700 dark:text-secondary-300"
        data-testid="motion-projection-dry-run-error"
      >
        Customer projection dry run unavailable: {error}
      </section>
    );
  }

  if (!payload) {
    return null;
  }

  const summary = payload.motion_projection_summary;
  const hasProjected = payload.projected_motion_statements.length > 0;

  return (
    <section
      className="space-y-4 rounded-lg border border-secondary-200 p-4 dark:border-secondary-700"
      data-testid="motion-projection-dry-run-panel"
      data-upload-id={uploadId}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">
          Customer Projection Dry Run
        </h3>
        <InternalOnlyBadge />
        <NotPublishedBadge />
      </div>
      <p className="text-xs text-secondary-500 dark:text-secondary-400">
        This is an internal dry-run preview. It does not publish to customers, reports, Evidence
        Reel, or delivery.
      </p>

      <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-200">
        Publisher ID
        <input
          className="mt-1 w-full rounded border border-secondary-300 bg-white p-2 text-sm dark:border-secondary-600 dark:bg-secondary-900"
          value={publisherId}
          onChange={(event) => onPublisherIdChange(event.target.value)}
          placeholder="admin:antoine"
          data-testid="motion-publication-publisher-id"
        />
      </label>

      <div className="flex flex-wrap gap-2 text-xs">
        <span
          className="rounded bg-secondary-100 px-2 py-1 font-mono text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200"
          data-testid="dry-run-publish-ready"
        >
          publish_ready=false
        </span>
        <span
          className="rounded bg-secondary-100 px-2 py-1 font-mono text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200"
          data-testid="dry-run-customer-visible"
        >
          customer_visible=false
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="rounded bg-secondary-50 p-2 dark:bg-secondary-800/60">
          <div className="font-medium text-secondary-500 dark:text-secondary-400">Eligible claims</div>
          <div
            className="text-lg font-semibold text-secondary-900 dark:text-white"
            data-testid="dry-run-eligible-count"
          >
            {summary.eligible_claim_count}
          </div>
        </div>
        <div className="rounded bg-secondary-50 p-2 dark:bg-secondary-800/60">
          <div className="font-medium text-secondary-500 dark:text-secondary-400">
            Projected statements
          </div>
          <div
            className="text-lg font-semibold text-secondary-900 dark:text-white"
            data-testid="dry-run-projected-count"
          >
            {summary.projected_statement_count}
          </div>
        </div>
        <div className="rounded bg-secondary-50 p-2 dark:bg-secondary-800/60">
          <div className="font-medium text-secondary-500 dark:text-secondary-400">Excluded claims</div>
          <div
            className="text-lg font-semibold text-secondary-900 dark:text-white"
            data-testid="dry-run-excluded-count"
          >
            {summary.excluded_claim_count}
          </div>
        </div>
      </div>

      {hasProjected ? (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
            Projected statements
          </h4>
          <div className="space-y-2">
            {payload.projected_motion_statements.map(renderProjectedStatement)}
          </div>
        </div>
      ) : (
        <p
          className="text-sm text-secondary-600 dark:text-secondary-300"
          data-testid="dry-run-empty-state"
        >
          No approved eligible motion claims are ready for dry-run projection.
        </p>
      )}

      {payload.excluded_claims.length > 0 ? (
        <details className="rounded-lg border border-secondary-200 p-3 dark:border-secondary-700">
          <summary
            className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400"
            data-testid="dry-run-excluded-toggle"
          >
            Excluded claims ({payload.excluded_claims.length})
          </summary>
          <div className="mt-3 space-y-2">{payload.excluded_claims.map(renderExcludedClaim)}</div>
        </details>
      ) : null}
    </section>
  );
};

interface MotionProjectionDryRunPanelProps {
  uploadId: string | null | undefined;
}

const MotionProjectionDryRunPanel: React.FC<MotionProjectionDryRunPanelProps> = ({
  uploadId,
}) => {
  const [payload, setPayload] = useState<MotionProjectionDryRunResponse | null>(null);
  const [publicationCandidatesByWindow, setPublicationCandidatesByWindow] = useState<
    Record<string, MotionPublicationCandidate>
  >({});
  const [insertionCandidatesByWindow, setInsertionCandidatesByWindow] = useState<
    Record<string, MotionReportInsertionCandidate>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publisherId, setPublisherId] = useState('');
  const [actionLoadingWindowId, setActionLoadingWindowId] = useState<string | null>(null);
  const [cardSuccessMessages, setCardSuccessMessages] = useState<Record<string, string>>({});
  const [insertionCardSuccessMessages, setInsertionCardSuccessMessages] = useState<
    Record<string, string>
  >({});

  const loadPanelData = useCallback(async () => {
    if (!uploadId) return;
    setLoading(true);
    setError(null);
    try {
      const [dryRun, publicationResponse, insertionResponse] = await Promise.all([
        fetchMotionClaimProjectionDryRun(uploadId),
        fetchMotionClaimPublicationCandidates(uploadId),
        fetchMotionClaimReportInsertionCandidates(uploadId),
      ]);
      setPayload(dryRun);
      const publicationByWindow: Record<string, MotionPublicationCandidate> = {};
      publicationResponse.candidates.forEach((candidate) => {
        publicationByWindow[candidate.window_id] = candidate;
      });
      setPublicationCandidatesByWindow(publicationByWindow);
      const insertionByWindow: Record<string, MotionReportInsertionCandidate> = {};
      insertionResponse.candidates.forEach((candidate) => {
        insertionByWindow[candidate.window_id] = candidate;
      });
      setInsertionCandidatesByWindow(insertionByWindow);
    } catch (err: unknown) {
      if (err instanceof AdminReviewApiError) {
        if (err.status === 404) {
          setError('Motion claim dry-run data not found for this upload');
        } else if (err.status === 403) {
          setError('Admin key missing or invalid');
        } else {
          setError(err.message);
        }
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to load customer projection dry run');
      }
      setPayload(null);
      setPublicationCandidatesByWindow({});
      setInsertionCandidatesByWindow({});
    } finally {
      setLoading(false);
    }
  }, [uploadId]);

  useEffect(() => {
    if (!uploadId) {
      setPayload(null);
      setPublicationCandidatesByWindow({});
      setInsertionCandidatesByWindow({});
      setError(null);
      return;
    }
    loadPanelData();
  }, [uploadId, loadPanelData]);

  const handleCreateCandidate = useCallback(
    async (windowId: string) => {
      if (!uploadId || !normalizeReviewerId(publisherId)) return;
      setActionLoadingWindowId(windowId);
      setError(null);
      try {
        await createMotionClaimPublicationCandidate(uploadId, windowId, {
          publisher_id: publisherId,
        });
        setCardSuccessMessages((current) => ({
          ...current,
          [windowId]: `Updated ${windowId}: Internal publish candidate created`,
        }));
        await loadPanelData();
      } catch (err: unknown) {
        if (err instanceof AdminReviewApiError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to create internal publish candidate');
        }
      } finally {
        setActionLoadingWindowId(null);
      }
    },
    [uploadId, publisherId, loadPanelData]
  );

  const handleRevokeCandidate = useCallback(
    async (windowId: string) => {
      if (!uploadId || !normalizeReviewerId(publisherId)) return;
      setActionLoadingWindowId(windowId);
      setError(null);
      try {
        await revokeMotionClaimPublicationCandidate(uploadId, windowId, {
          publisher_id: publisherId,
          revoke_reason: 'Revoked from admin dry-run panel',
        });
        setCardSuccessMessages((current) => ({
          ...current,
          [windowId]: `Updated ${windowId}: Internal publish candidate revoked`,
        }));
        await loadPanelData();
      } catch (err: unknown) {
        if (err instanceof AdminReviewApiError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to revoke internal publish candidate');
        }
      } finally {
        setActionLoadingWindowId(null);
      }
    },
    [uploadId, publisherId, loadPanelData]
  );

  const handleCreateInsertionCandidate = useCallback(
    async (windowId: string) => {
      if (!uploadId || !normalizeReviewerId(publisherId)) return;
      setActionLoadingWindowId(windowId);
      setError(null);
      try {
        await createMotionClaimReportInsertionCandidate(uploadId, windowId, {
          inserted_by: publisherId,
          insertion_note: 'Created from admin dry-run panel',
        });
        setInsertionCardSuccessMessages((current) => ({
          ...current,
          [windowId]: `Updated ${windowId}: Customer report insertion candidate created`,
        }));
        await loadPanelData();
      } catch (err: unknown) {
        if (err instanceof AdminReviewApiError) {
          if (err.status === 409) {
            setError(`An active report insertion candidate already exists for ${windowId}.`);
          } else {
            setError(err.message);
          }
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to create report insertion candidate');
        }
      } finally {
        setActionLoadingWindowId(null);
      }
    },
    [uploadId, publisherId, loadPanelData]
  );

  const handleRevokeInsertionCandidate = useCallback(
    async (windowId: string) => {
      if (!uploadId || !normalizeReviewerId(publisherId)) return;
      setActionLoadingWindowId(windowId);
      setError(null);
      try {
        await revokeMotionClaimReportInsertionCandidate(uploadId, windowId, {
          inserted_by: publisherId,
          revoke_reason: 'Revoked from admin dry-run panel',
        });
        setInsertionCardSuccessMessages((current) => ({
          ...current,
          [windowId]: `Updated ${windowId}: Customer report insertion candidate revoked`,
        }));
        await loadPanelData();
      } catch (err: unknown) {
        if (err instanceof AdminReviewApiError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to revoke report insertion candidate');
        }
      } finally {
        setActionLoadingWindowId(null);
      }
    },
    [uploadId, publisherId, loadPanelData]
  );

  if (!uploadId) {
    return null;
  }

  return (
    <MotionProjectionDryRunPanelView
      uploadId={uploadId}
      payload={payload}
      loading={loading}
      error={error}
      publisherId={publisherId}
      onPublisherIdChange={setPublisherId}
      publicationCandidatesByWindow={publicationCandidatesByWindow}
      insertionCandidatesByWindow={insertionCandidatesByWindow}
      actionLoadingWindowId={actionLoadingWindowId}
      cardSuccessMessages={cardSuccessMessages}
      insertionCardSuccessMessages={insertionCardSuccessMessages}
      onCreateCandidate={handleCreateCandidate}
      onRevokeCandidate={handleRevokeCandidate}
      onCreateInsertionCandidate={handleCreateInsertionCandidate}
      onRevokeInsertionCandidate={handleRevokeInsertionCandidate}
    />
  );
};

export default MotionProjectionDryRunPanel;
